import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Landing from "./pages/Landing";
import BibleProjectPage from "./pages/BibleProjectPage";
import FlipbookPdfExportPage from "./pages/FlipbookPdfExportPage";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Dashboard from "./pages/Dashboard";
import CheckoutSuccess from "./pages/CheckoutSuccess";
import FounderStory from "./pages/FounderStory";
import FunnelPublic from "./pages/FunnelPublic";
import FunnelBuilder from "./pages/FunnelBuilder";
import PublicProfile from "./pages/PublicProfile";
import DirectoryPage from "./pages/DirectoryPage";
import HonestTruth from "./pages/HonestTruth";
import GoliPage from "./pages/GoliPage";
import MarketPage from "./pages/MarketPage";
import JukeboxPage from "./pages/JukeboxPage";
import ColoringPagesPage from "./pages/ColoringPagesPage";
import PiurifyPage from "./pages/PiurifyPage";
// Removed Feb 2026 — Mike pulled LiveGood (MLM concerns) and Brainzyme.
// Routes now redirect to /market.
import RcRacersPage from "./pages/RcRacersPage";
// LiveGoodPage import removed (page deleted Feb 2026).
// CoachCertificationPage import removed (Mike: "forget all that coach certification" — Feb 2026).
// FunnelramaPage removed — was a pointless brochure page. /funnelrama redirects to /pricing.
import AffiliateMarketing101Page from "./pages/AffiliateMarketing101Page";
import WriteABookIn7DaysPage from "./pages/WriteABookIn7DaysPage";
import AdminTutorialsPage from "./pages/AdminTutorialsPage";
import AskMentorPage from "./pages/AskMentorPage";
import PoofyPage from "./pages/PoofyPage";
import LibraryPage from "./pages/LibraryPage";
import AffiliateProgramsPage from "./pages/AffiliateProgramsPage";
import MagicLoginPage from "./pages/MagicLoginPage";
import CriticalToolsPage from "./pages/CriticalToolsPage";
import WriterChatPage from "./pages/WriterChatPage";
import BookCoverPage from "./pages/BookCoverPage";
import AdminBookCoverOrdersPage from "./pages/AdminBookCoverOrdersPage";
import FreePhotosPage from "./pages/FreePhotosPage";
import AppointmentsPage from "./pages/AppointmentsPage";
import BookingPublicPage from "./pages/BookingPublicPage";
import AdminWithFaithChaptersPage from "./pages/AdminWithFaithChaptersPage";
import WalkPage from "./pages/WalkPage";
import MirrorSite from "./pages/MirrorSite";
import MirrorSiteProduct from "./pages/MirrorSiteProduct";
import FlipbookViewer from "./pages/FlipbookViewer";
import MemberStorefront from "./pages/MemberStorefront";
import ReadArticle from "./pages/ReadArticle";
import FlipbookPrint from "./pages/FlipbookPrint";
import MasterBookPrint from "./pages/MasterBookPrint";
import PrayerGiftLanding from "./pages/PrayerGiftLanding";
import FreeChallengePdfLanding from "./pages/FreeChallengePdfLanding";
import StarterGuidePage from "./pages/StarterGuidePage";
import FreeBookPage from "./pages/FreeBookPage";
import FreeGuidePage from "./pages/FreeGuidePage";
import VendorStandardsPage from "./pages/VendorStandardsPage";
import MiniLessonDemoPage from "./pages/MiniLessonDemoPage";
import AudioLessonDemoPage from "./pages/AudioLessonDemoPage";
import MallMarketPage from "./pages/MallMarketPage";
import ToolIntrosHubPage from "./pages/ToolIntrosHubPage";
import ToolIntroPlayerPage from "./pages/ToolIntroPlayerPage";
import MyStarterGuidePage from "./pages/MyStarterGuidePage";
import MyProductsPage from "./pages/MyProductsPage";
import BuyProductPage from "./pages/BuyProductPage";
import AIWriterPage from "./pages/AIWriterPage";
import VendorSalesAdminPage from "./pages/VendorSalesAdminPage";
import DonatePage, { DonateThankYou } from "./pages/DonatePage";
import WhatYouEarnPage from "./pages/WhatYouEarnPage";
import EpubReaderPage from "./pages/EpubReaderPage";
import TrainingHubPage from "./pages/TrainingHubPage";
import TrainingCoursePage from "./pages/TrainingCoursePage";
import TrainingScriptsPrintPage from "./pages/TrainingScriptsPrintPage";
import WithFaithBookPage from "./pages/WithFaithBookPage";
import ScrollToTop from "./components/ScrollToTop";
import RefCapture from "./components/RefCapture";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-red-wine font-serif-display text-2xl">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <RefCapture />
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/bibleproject" element={<BibleProjectPage />} />
          <Route path="/admin/flipbook-pdfs" element={<FlipbookPdfExportPage />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/starter-guide" element={<StarterGuidePage />} />
          <Route path="/free-book" element={<FreeBookPage />} />
          <Route path="/free-guide" element={<FreeGuidePage />} />
          <Route path="/vendor-standards" element={<VendorStandardsPage />} />
          <Route path="/submit-product" element={<VendorStandardsPage />} />
          <Route path="/vendors" element={<VendorStandardsPage />} />
          <Route path="/training/demo"   element={<MiniLessonDemoPage />} />
          <Route path="/training/audio-demo" element={<AudioLessonDemoPage />} />
          <Route path="/audio-demo"      element={<AudioLessonDemoPage />} />
          <Route path="/market"          element={<MallMarketPage />} />
          <Route path="/mall"            element={<Navigate to="/market" replace />} />
          <Route path="/market-mall"     element={<Navigate to="/market" replace />} />
          <Route path="/the-mall"        element={<Navigate to="/market" replace />} />
          <Route path="/mini-lesson"     element={<MiniLessonDemoPage />} />
          <Route path="/training-format" element={<MiniLessonDemoPage />} />
          <Route path="/training/tool-intros"        element={<ToolIntrosHubPage />} />
          <Route path="/training/tool-intros/:slug"  element={<ToolIntroPlayerPage />} />
          <Route path="/tool-intros"                 element={<ToolIntrosHubPage />} />
          <Route path="/tool-intros/:slug"           element={<ToolIntroPlayerPage />} />
          <Route path="/free-bundle" element={<Navigate to="/free-book" replace />} />
          <Route path="/starter" element={<Navigate to="/starter-guide" replace />} />
          <Route path="/dashboard/starter-guide" element={<Protected><MyStarterGuidePage /></Protected>} />
          <Route path="/my/starter-guide" element={<Navigate to="/dashboard/starter-guide" replace />} />
          <Route path="/dashboard/my-products" element={<Protected><MyProductsPage /></Protected>} />
          <Route path="/buy/:pid" element={<BuyProductPage />} />
          <Route path="/buy/:pid/success" element={<BuyProductPage />} />
          <Route path="/founder" element={<FounderStory />} />
          <Route path="/books" element={<Navigate to="/" replace />} />
          <Route path="/directory" element={<DirectoryPage />} />
          <Route path="/honest-truth" element={<HonestTruth />} />
          <Route path="/goli" element={<GoliPage />} />
          <Route path="/piurify" element={<PiurifyPage />} />
          <Route path="/brainzyme" element={<Navigate to="/market" replace />} />
          <Route path="/rc-racers" element={<RcRacersPage />} />
          <Route path="/rcracers" element={<Navigate to="/rc-racers" replace />} />
          <Route path="/touchstone" element={<Navigate to="/market" replace />} />
          <Route path="/touchstone-essentials" element={<Navigate to="/market" replace />} />
          <Route path="/touchstone/calculator" element={<Navigate to="/market" replace />} />
          <Route path="/livegood" element={<Navigate to="/market" replace />} />
          <Route path="/live-good" element={<Navigate to="/market" replace />} />
          <Route path="/dashboard/ai-writer" element={<Protected><AIWriterPage /></Protected>} />
          <Route path="/dashboard/vendor-sales" element={<Protected><VendorSalesAdminPage /></Protected>} />
          <Route path="/read/:slug" element={<EpubReaderPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/dashboard/ask-mike" element={<Protected><AskMentorPage /></Protected>} />
          <Route path="/ask-mike" element={<Navigate to="/dashboard/ask-mike" replace />} />
          <Route path="/poofy" element={<PoofyPage />} />
          <Route path="/dashboard/critical-tools" element={<Protected><CriticalToolsPage /></Protected>} />
          <Route path="/dashboard/affiliate-programs" element={<Protected><AffiliateProgramsPage /></Protected>} />
          <Route path="/auth/magic" element={<MagicLoginPage />} />
          <Route path="/dashboard/writer-chat" element={<Protected><WriterChatPage /></Protected>} />
          <Route path="/dashboard/book-cover" element={<Protected><BookCoverPage /></Protected>} />
          <Route path="/admin/book-cover-orders" element={<AdminBookCoverOrdersPage />} />
          <Route path="/dashboard/free-photos" element={<Protected><FreePhotosPage /></Protected>} />
          <Route path="/dashboard/appointments" element={<Protected><AppointmentsPage /></Protected>} />
          <Route path="/m/:handle/book" element={<BookingPublicPage />} />
          <Route path="/ai-writer" element={<Navigate to="/dashboard/ai-writer" replace />} />
          <Route path="/coach-certification" element={<Navigate to="/" replace />} />
          <Route path="/certification" element={<Navigate to="/" replace />} />
          <Route path="/coach-cert" element={<Navigate to="/" replace />} />
          <Route path="/book/plastic-detox" element={<Navigate to="/" replace />} />
          <Route path="/funnelrama" element={<Navigate to="/pricing" replace />} />
          <Route path="/recording-studio" element={<Navigate to="/training" replace />} />
          <Route path="/scripts" element={<Navigate to="/training" replace />} />
          <Route path="/scene-library" element={<Navigate to="/training" replace />} />
          <Route path="/scenes" element={<Navigate to="/training" replace />} />
          <Route path="/write-a-book-in-7-days" element={<Navigate to="/" replace />} />
          <Route path="/business/affiliate-marketing-101" element={<AffiliateMarketing101Page />} />
          <Route path="/pro-vault/affiliate-marketing-101" element={<Navigate to="/business/affiliate-marketing-101" replace />} />
          <Route path="/academy" element={<TrainingHubPage />} />
          <Route path="/academy/:slug/scripts-print" element={<TrainingScriptsPrintPage />} />
          <Route path="/academy/:slug" element={<TrainingCoursePage />} />
          <Route path="/training" element={<Navigate to="/academy" replace />} />
          <Route path="/training/scripts" element={<Navigate to="/academy" replace />} />
          <Route path="/training/:slug/scripts-print" element={<TrainingScriptsPrintPage />} />
          <Route path="/training/:slug" element={<TrainingCoursePage />} />
          <Route path="/with-faith" element={<WithFaithBookPage />} />
          <Route path="/the-book" element={<Navigate to="/" replace />} />
          <Route path="/apologetics" element={<Navigate to="/" replace />} />
          <Route path="/apologetics/:slug" element={<Navigate to="/" replace />} />
          <Route path="/series" element={<Navigate to="/" replace />} />
          <Route path="/series/:slug" element={<Navigate to="/" replace />} />
          <Route path="/admin/tutorials" element={<AdminTutorialsPage />} />
          <Route path="/admin/with-faith-chapters" element={<AdminWithFaithChaptersPage />} />
          <Route path="/coloring-pages" element={<ColoringPagesPage />} />
          <Route path="/kids" element={<Navigate to="/coloring-pages" replace />} />
          <Route path="/walk" element={<WalkPage />} />
          <Route path="/m/:handle" element={<MirrorSite />} />
          <Route path="/m/:handle/:slug" element={<MirrorSiteProduct />} />
          <Route path="/book/:slug/:handle" element={<FlipbookViewer />} />
          <Route path="/book/:slug" element={<FlipbookViewer />} />
          <Route path="/market" element={<MallMarketPage />} />
          <Route path="/market-old" element={<MarketPage />} />
          <Route path="/jukebox" element={<JukeboxPage />} />
          {/* Old URLs people may have shared — redirect, don't 404 */}
          <Route path="/affilirama" element={<Navigate to="/market" replace />} />
          <Route path="/partners" element={<Navigate to="/market" replace />} />
          <Route path="/donate" element={<DonatePage />} />
          <Route path="/donate/thank-you" element={<DonateThankYou />} />
          <Route path="/what-you-earn" element={<WhatYouEarnPage />} />
          <Route path="/earn" element={<Navigate to="/what-you-earn" replace />} />
          <Route path="/commissions" element={<Navigate to="/what-you-earn" replace />} />
          <Route path="/u/:username" element={<PublicProfile />} />
          <Route path="/u/:username/store" element={<MemberStorefront />} />
          <Route path="/read/:slug" element={<Navigate to="/" replace />} />
          <Route path="/flipbook/:slug" element={<Navigate to="/" replace />} />
          <Route path="/free-gift/praying-for-intimacy" element={<Navigate to="/" replace />} />
          <Route path="/free-gift/30-day-challenge" element={<Navigate to="/" replace />} />
          <Route path="/flipbook/:slug/print" element={<FlipbookPrint />} />
          <Route path="/flipbook/master-book/print" element={<MasterBookPrint />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/dashboard/*" element={<Protected><Dashboard /></Protected>} />
          <Route path="/checkout/success" element={<Protected><CheckoutSuccess /></Protected>} />
          <Route path="/f/:id" element={<FunnelPublic />} />
          <Route path="/builder/:id" element={<Protected><FunnelBuilder /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
